import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ResponsiveContainer from '../components/ui/ResponsiveContainer';
import BasePageLayout from '../components/BasePageLayout';
import CountrySettingsComponent from '../components/ui/CountrySettingsComponent';
import { supabase } from '../services/supabaseClient';
import { TeamService } from '../services/teamService';
import { WebsiteMessageService, WebsiteMessage } from '../services/websiteMessageService';
import { AdminMessageFormData, AdminManageableMessageType } from '../types/websiteMessage';
import { TeamSettings } from '../types/team';
import ProjectCreationSettingsTab from '../components/admin/ProjectCreationSettingsTab';
import '../styles/pages/SettingsPage.css';

interface PointsRule {
  id: string;
  rule_type: string;
  description: string;
  points: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, resetTour, triggerTour, tourLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<'points' | 'countries' | 'referrals' | 'teams' | 'messages' | 'project_creation' | 'preferences'>('points');
  const [pointsRules, setPointsRules] = useState<PointsRule[]>([]);
  const [teamSettings, setTeamSettings] = useState<TeamSettings[]>([]);
  const [websiteMessages, setWebsiteMessages] = useState<WebsiteMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingRule, setEditingRule] = useState<PointsRule | null>(null);
  const [editingMessage, setEditingMessage] = useState<WebsiteMessage | null>(null);
  const [showAddRule, setShowAddRule] = useState(false);
  const [showAddMessage, setShowAddMessage] = useState(false);
  const [referralStats, setReferralStats] = useState({
    total_referrals: 0,
    points_awarded: 0,
    referrals_this_month: 0
  });
  const [tourResetSuccess, setTourResetSuccess] = useState<string | null>(null);

  // Form states for editing/adding rules
  const [formData, setFormData] = useState({
    rule_type: '',
    description: '',
    points: 0,
    is_active: true
  });

  // Form states for editing/adding messages
  const [messageFormData, setMessageFormData] = useState<AdminMessageFormData>({
    message_text: '',
    message_type: 'welcome',
    background_color: '#e3f2fd',
    text_color: '#1976d2',
    start_date: '',
    end_date: '',
    is_active: true
  });

  useEffect(() => {
    fetchPointsRules();
    if (activeTab === 'referrals') {
      fetchReferralStats();
    } else if (activeTab === 'teams') {
      fetchTeamSettings();
    } else if (activeTab === 'messages') {
      fetchWebsiteMessages();
    }
  }, [activeTab]);

  // Early return if user is not logged in
  if (!user) {
    return (
      <ResponsiveContainer>
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Please log in to access settings.
        </div>
      </ResponsiveContainer>
    );
  }

  const fetchPointsRules = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('point_rules')
        .select('*')
        .order('rule_type');

      if (error) throw error;
      setPointsRules(data || []);
    } catch (err: any) {
      console.error('Error fetching points rules:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferralStats = async () => {
    try {
      const { data, error } = await supabase.rpc('get_referral_stats');
      if (error) throw error;
      
      if (data && data.length > 0) {
        setReferralStats({
          total_referrals: parseInt(data[0].total_referrals) || 0,
          points_awarded: parseInt(data[0].points_awarded) || 0,
          referrals_this_month: parseInt(data[0].referrals_this_month) || 0
        });
      }
    } catch (err: any) {
      console.error('Error fetching referral stats:', err);
    }
  };

  const fetchTeamSettings = async () => {
    try {
      setLoading(true);
      const settings = await TeamService.getTeamSettings();
      setTeamSettings(settings);
    } catch (err: any) {
      console.error('Error fetching team settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Website Messages functions
  const fetchWebsiteMessages = async () => {
    try {
      setLoading(true);
      const messages = await WebsiteMessageService.getAdminManageableMessages();
      setWebsiteMessages(messages);
    } catch (error) {
      console.error('Error fetching website messages:', error);
      setError('Failed to fetch website messages');
    } finally {
      setLoading(false);
    }
  };

  const handleEditMessage = (message: WebsiteMessage) => {
    setEditingMessage(message);
    setMessageFormData({
      message_text: message.message_text,
      message_type: message.message_type as AdminManageableMessageType,
      background_color: message.background_color,
      text_color: message.text_color,
      start_date: message.start_date ? new Date(message.start_date).toISOString().slice(0, 16) : '',
      end_date: message.end_date ? new Date(message.end_date).toISOString().slice(0, 16) : '',
      is_active: message.is_active
    });
    setShowAddMessage(false);
  };

  const handleAddNewMessage = () => {
    setEditingMessage(null);
    setMessageFormData({
      message_text: '',
      message_type: 'welcome',
      background_color: '#e3f2fd',
      text_color: '#1976d2',
      start_date: new Date().toISOString().slice(0, 16),
      end_date: '',
      is_active: true
    });
    setShowAddMessage(true);
  };

  const handleSaveMessage = async () => {
    try {
      if (editingMessage) {
        // Update existing message
        await WebsiteMessageService.updateMessage(editingMessage.id, {
          message_text: messageFormData.message_text,
          message_type: messageFormData.message_type,
          background_color: messageFormData.background_color,
          text_color: messageFormData.text_color,
          start_date: messageFormData.start_date ? new Date(messageFormData.start_date).toISOString() : '',
          end_date: messageFormData.end_date ? new Date(messageFormData.end_date).toISOString() : null
        });
      } else {
        // Add new message
        await WebsiteMessageService.createMessage({
          message_text: messageFormData.message_text,
          message_type: messageFormData.message_type,
          background_color: messageFormData.background_color,
          text_color: messageFormData.text_color,
          start_date: messageFormData.start_date ? new Date(messageFormData.start_date).toISOString() : new Date().toISOString(),
          end_date: messageFormData.end_date ? new Date(messageFormData.end_date).toISOString() : null
        });
      }

      await fetchWebsiteMessages();
      setEditingMessage(null);
      setShowAddMessage(false);
      setError(null);
    } catch (error) {
      console.error('Error saving message:', error);
      setError('Failed to save message');
    }
  };

  const handleToggleMessageStatus = async (messageId: string, isActive: boolean) => {
    try {
      await WebsiteMessageService.toggleMessageStatus(messageId, isActive);
      await fetchWebsiteMessages();
    } catch (error) {
      console.error('Error toggling message status:', error);
      setError('Failed to update message status');
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await WebsiteMessageService.deleteMessage(messageId);
        await fetchWebsiteMessages();
      } catch (error) {
        console.error('Error deleting message:', error);
        setError('Failed to delete message');
      }
    }
  };

  const handleEditRule = (rule: PointsRule) => {
    setEditingRule(rule);
    setFormData({
      rule_type: rule.rule_type,
      description: rule.description,
      points: rule.points,
      is_active: rule.is_active
    });
    setShowAddRule(false);
  };

  const handleAddNewRule = () => {
    setEditingRule(null);
    setFormData({
      rule_type: '',
      description: '',
      points: 0,
      is_active: true
    });
    setShowAddRule(true);
  };

  const handleSaveRule = async () => {
    try {
      if (editingRule) {
        // Update existing rule
        const { error } = await supabase
          .from('point_rules')
          .update({
            description: formData.description,
            points: formData.points,
            is_active: formData.is_active,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingRule.id);

        if (error) throw error;
      } else {
        // Add new rule
        const { error } = await supabase
          .from('point_rules')
          .insert([{
            rule_name: formData.rule_type,
            rule_type: formData.rule_type,
            description: formData.description,
            points: formData.points,
            is_active: formData.is_active
          }]);

        if (error) throw error;
      }

      await fetchPointsRules();
      setEditingRule(null);
      setShowAddRule(false);
      setError(null);
    } catch (err: any) {
      console.error('Error saving rule:', err);
      setError(err.message);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;

    try {
      const { error } = await supabase
        .from('point_rules')
        .delete()
        .eq('id', ruleId);

      if (error) throw error;
      await fetchPointsRules();
    } catch (err: any) {
      console.error('Error deleting rule:', err);
      setError(err.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingRule(null);
    setShowAddRule(false);
    setFormData({
      rule_type: '',
      description: '',
      points: 0,
      is_active: true
    });
  };

  const handleUpdateTeamSetting = async (id: string, value: number) => {
    try {
      await TeamService.updateTeamSetting(id, value);
      setTeamSettings(prev => prev.map(setting => 
        setting.id === id ? { ...setting, setting_value: value } : setting
      ));
    } catch (err: any) {
      console.error('Error updating team setting:', err);
      setError(err.message);
    }
  };

  const handleRestartTour = async () => {
    if (!window.confirm('Are you sure you want to restart the onboarding tour? This will reset your tour progress and show the tour again.')) {
      return;
    }

    try {
      setError(null);
      setTourResetSuccess(null);
      const result = await resetTour();
      
      if (result.success) {
        // Trigger the tour and navigate to home page immediately
        triggerTour();
        navigate('/');
      } else {
        setError(result.error?.message || 'Failed to reset tour');
      }
    } catch (err: any) {
      console.error('Error restarting tour:', err);
      setError(err.message || 'Failed to reset tour');
    }
  };

  const renderPointsRulesTab = () => (
    <div className="points-rules-tab">
      <div className="tab-header">
        <h3>Points Rules Management</h3>        <button 
          className="btn btn-primary"
          onClick={handleAddNewRule}
          disabled={showAddRule || !!editingRule}
        >
          <i className="bi bi-plus-circle me-2"></i>Add New Rule
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {(showAddRule || editingRule) && (
        <div className="rule-form">
          <h4>{editingRule ? 'Edit Rule' : 'Add New Rule'}</h4>
          <div className="row">
            <div className="col-md-6">
              <div className="form-group mb-3">
                <label className="form-label">Action Type</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.rule_type}
                  onChange={(e) => setFormData({...formData, rule_type: e.target.value})}
                  placeholder="e.g., task_completion, daily_login"
                  disabled={!!editingRule}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group mb-3">
                <label className="form-label">Points</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.points}
                  onChange={(e) => setFormData({...formData, points: parseInt(e.target.value) || 0})}
                  min="0"
                />
              </div>
            </div>
          </div>
          <div className="form-group mb-3">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
              placeholder="Describe when this rule applies"
            />
          </div>
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              id="isActive"
            />
            <label className="form-check-label" htmlFor="isActive">
              Rule is active
            </label>
          </div>
          <div className="form-actions">
            <button className="btn btn-success me-2" onClick={handleSaveRule}>
              <i className="bi bi-check-circle me-2"></i>
              {editingRule ? 'Update Rule' : 'Add Rule'}
            </button>
            <button className="btn btn-secondary" onClick={handleCancelEdit}>
              <i className="bi bi-x-circle me-2"></i>Cancel
            </button>
          </div>
        </div>
      )}

      <div className="rules-list">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-dark">
                <tr>
                  <th>Action Type</th>
                  <th>Description</th>
                  <th>Points</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pointsRules.map((rule) => (
                  <tr key={rule.id}>
                    <td>
                      <span className="badge bg-primary">{rule.rule_type}</span>
                    </td>
                    <td>{rule.description}</td>
                    <td>
                      <span className="points-value">{rule.points} points</span>
                    </td>
                    <td>
                      <span className={`badge ${rule.is_active ? 'bg-success' : 'bg-secondary'}`}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleEditRule(rule)}
                          disabled={showAddRule || !!editingRule}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteRule(rule.id)}
                          disabled={showAddRule || !!editingRule}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderCountriesTab = () => (
    <div className="countries-tab">
      <div className="tab-header">
        <h3>Country Settings</h3>
        <p className="text-muted">Manage available countries for tasks and questionnaires</p>
      </div>
      <CountrySettingsComponent />
    </div>
  );

  const renderReferralsTab = () => (
    <div className="referrals-tab">
      <div className="tab-header">
        <h3>Referral System</h3>
        <p className="text-muted">Configure referral bonuses and tracking</p>
      </div>
      <div className="referral-settings">
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          Referral system allows users to earn points by inviting others. The referral bonus rule should be configured in the Points Rules tab.
        </div>
        <div className="referral-stats">
          <h4>Referral Statistics</h4>
          <div className="row">
            <div className="col-md-4">
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="bi bi-people-fill"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-number">{referralStats.total_referrals}</div>
                  <div className="stat-label">Total Referrals</div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="bi bi-star-fill"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-number">{referralStats.points_awarded}</div>
                  <div className="stat-label">Points Awarded</div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="bi bi-calendar-check"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-number">{referralStats.referrals_this_month}</div>
                  <div className="stat-label">This Month</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTeamsTab = () => (
    <div className="teams-tab">
      <div className="tab-header">
        <h3>Team Building Settings</h3>
        <p className="text-muted">Configure team formation rules and costs</p>
      </div>
      
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="team-settings">
          <div className="alert alert-info">
            <i className="bi bi-info-circle me-2"></i>
            These settings control the team building feature costs and limits.
          </div>
          
          <div className="settings-grid">
            {teamSettings.map((setting) => (
              <div key={setting.id} className="setting-card">
                <div className="setting-info">
                  <h5>{setting.setting_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h5>
                  <p className="text-muted">{setting.description}</p>
                </div>
                <div className="setting-control">
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      value={setting.setting_value}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        handleUpdateTeamSetting(setting.id, value);
                      }}
                      min="0"
                    />
                    <span className="input-group-text">
                      {setting.setting_name.includes('cost') ? 'points' : 'members'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {error && (
        <div className="alert alert-danger mt-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}
    </div>
  );

  const renderPreferencesTab = () => (
    <div className="preferences-tab">
      <div className="tab-header">
        <h3>User Preferences</h3>
        <p className="text-muted">Manage your account preferences and tour settings</p>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {tourResetSuccess && (
        <div className="alert alert-success">
          <i className="bi bi-check-circle me-2"></i>
          {tourResetSuccess}
        </div>
      )}

      <div className="preferences-section">
        <div className="preference-card">
          <div className="preference-header">
            <h4>
              <i className="bi bi-compass me-2"></i>
              Onboarding Tour
            </h4>
            <p className="text-muted">
              Restart the guided tour to learn about SeedDots's features again
            </p>
          </div>
          
          <div className="preference-content">
            <div className="tour-info">
              <div className="info-item">
                <i className="bi bi-info-circle text-primary"></i>
                <span>The tour will guide you through all platform features</span>
              </div>
              <div className="info-item">
                <i className="bi bi-clock text-primary"></i>
                <span>Takes approximately 5-10 minutes to complete</span>
              </div>
              <div className="info-item">
                <i className="bi bi-skip-forward text-primary"></i>
                <span>You can skip or pause the tour at any time</span>
              </div>
            </div>
            
            <div className="preference-actions">
              <button
                className="btn btn-primary"
                onClick={handleRestartTour}
                disabled={tourLoading}
              >
                {tourLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Restarting Tour...
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Restart Onboarding Tour
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="preference-card">
          <div className="preference-header">
            <h4>
              <i className="bi bi-gear me-2"></i>
              Account Settings
            </h4>
            <p className="text-muted">
              Additional account preferences and settings
            </p>
          </div>
          
          <div className="preference-content">
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              More user preferences will be available in future updates.
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMessagesTab = () => (
    <div className="messages-tab">
      <div className="tab-header">
        <h3>Website Messages Management</h3>
        <button 
          className="btn btn-primary"
          onClick={handleAddNewMessage}
          disabled={showAddMessage || !!editingMessage}
        >
          <i className="bi bi-plus-circle me-2"></i>Add New Message
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {(showAddMessage || editingMessage) && (
        <div className="message-form">
          <h4>{editingMessage ? 'Edit Message' : 'Add New Message'}</h4>
          <div className="row">
            <div className="col-md-12">
              <div className="form-group mb-3">
                <label className="form-label">Message Text</label>
                <textarea
                  className="form-control"
                  value={messageFormData.message_text}
                  onChange={(e) => setMessageFormData({...messageFormData, message_text: e.target.value})}
                  placeholder="Enter your website message..."
                  rows={3}
                  maxLength={500}
                />
                <small className="form-text text-muted">
                  {messageFormData.message_text.length}/500 characters
                </small>
              </div>
            </div>
          </div>
          
          <div className="row">
            <div className="col-md-6">
              <div className="form-group mb-3">
                <label className="form-label">Message Type</label>
                <select
                  className="form-control"
                  value={messageFormData.message_type}
                  onChange={(e) => setMessageFormData({...messageFormData, message_type: e.target.value as AdminManageableMessageType})}
                >
                  <option value="welcome">Welcome</option>
                  <option value="announcement">Announcement</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="info">Information</option>
                </select>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group mb-3">
                <label className="form-label">Status</label>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={messageFormData.is_active}
                    onChange={(e) => setMessageFormData({...messageFormData, is_active: e.target.checked})}
                  />
                  <label className="form-check-label">
                    Active (visible to users)
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          <div className="row">
            <div className="col-md-6">
              <div className="form-group mb-3">
                <label className="form-label">Background Color</label>
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={messageFormData.background_color}
                  onChange={(e) => setMessageFormData({...messageFormData, background_color: e.target.value})}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group mb-3">
                <label className="form-label">Text Color</label>
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={messageFormData.text_color}
                  onChange={(e) => setMessageFormData({...messageFormData, text_color: e.target.value})}
                />
              </div>
            </div>
          </div>
          
          <div className="row">
            <div className="col-md-6">
              <div className="form-group mb-3">
                <label className="form-label">Start Date (Optional)</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={messageFormData.start_date}
                  onChange={(e) => setMessageFormData({...messageFormData, start_date: e.target.value})}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group mb-3">
                <label className="form-label">End Date (Optional)</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={messageFormData.end_date}
                  onChange={(e) => setMessageFormData({...messageFormData, end_date: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="form-group mb-3">
            <label className="form-label">Preview</label>
            <div 
              className="message-preview"
              style={{
                backgroundColor: messageFormData.background_color,
                color: messageFormData.text_color,
                border: `1px solid ${messageFormData.text_color}`,
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <i className="bi bi-chat-dots"></i>
              <span>{messageFormData.message_text || 'Your message will appear here...'}</span>
            </div>
          </div>

          <div className="form-actions">
            <button 
              className="btn btn-primary me-2"
              onClick={handleSaveMessage}
              disabled={!messageFormData.message_text.trim()}
            >
              <i className="bi bi-check-circle me-2"></i>
              {editingMessage ? 'Update Message' : 'Save Message'}
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => {
                setEditingMessage(null);
                setShowAddMessage(false);
              }}
            >
              <i className="bi bi-x-circle me-2"></i>Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="messages-list">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Message</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Dates</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {websiteMessages.map((message) => (
                  <tr key={message.id}>
                    <td>
                      <div 
                        className="message-preview-small"
                        style={{
                          backgroundColor: message.background_color,
                          color: message.text_color,
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          maxWidth: '300px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {message.message_text}
                      </div>
                    </td>
                    <td>
                      <span className={`badge bg-${
                        message.message_type === 'welcome' ? 'primary' : 
                        message.message_type === 'announcement' ? 'warning' : 
                        message.message_type === 'maintenance' ? 'danger' : 'info'
                      }`}>
                        {message.message_type}
                      </span>
                    </td>
                    <td>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={message.is_active}
                          onChange={(e) => handleToggleMessageStatus(message.id, e.target.checked)}
                        />
                        <label className="form-check-label">
                          {message.is_active ? 'Active' : 'Inactive'}
                        </label>
                      </div>
                    </td>
                    <td>
                      <small>
                        <div>Start: {new Date(message.start_date).toLocaleDateString()}</div>
                        {message.end_date && message.end_date !== null && (
                          <div>End: {new Date(message.end_date).toLocaleDateString()}</div>
                        )}
                      </small>
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => handleEditMessage(message)}
                          disabled={!!editingMessage || showAddMessage}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => handleDeleteMessage(message.id)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {websiteMessages.length === 0 && (
              <div className="text-center py-4 text-muted">
                <i className="bi bi-chat-square-dots fs-1 mb-3"></i>
                <p>No website messages found. Create your first message!</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <BasePageLayout>
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <i className="bi bi-gear-fill"></i>
          </div>
          <div className="header-text">
            <h1>Admin Settings</h1>
            <p>Manage system settings and configurations</p>
          </div>
        </div>
      </div>

      <div className="settings-tabs-container">
        <div className="professional-tabs">
          <div className="tab-buttons">
            <button
              className={`tab-button ${activeTab === 'points' ? 'active' : ''}`}
              onClick={() => setActiveTab('points')}
            >
              <i className="bi bi-star-fill"></i>
              Points Rules
            </button>
            <button
              className={`tab-button ${activeTab === 'countries' ? 'active' : ''}`}
              onClick={() => setActiveTab('countries')}
            >
              <i className="bi bi-globe"></i>
              Countries
            </button>
            <button
              className={`tab-button ${activeTab === 'referrals' ? 'active' : ''}`}
              onClick={() => setActiveTab('referrals')}
            >
              <i className="bi bi-people-fill"></i>
              Referrals
            </button>
            <button
              className={`tab-button ${activeTab === 'teams' ? 'active' : ''}`}
              onClick={() => setActiveTab('teams')}
            >
              <i className="bi bi-person-check-fill"></i>
              Teams
            </button>
            <button
              className={`tab-button ${activeTab === 'messages' ? 'active' : ''}`}
              onClick={() => setActiveTab('messages')}
            >
              <i className="bi bi-chat-square-dots"></i>
              Messages
            </button>
            <button
              className={`tab-button ${activeTab === 'project_creation' ? 'active' : ''}`}
              onClick={() => setActiveTab('project_creation')}
            >
              <i className="bi bi-rocket-takeoff"></i>
              Project Creation
            </button>
            <button
              className={`tab-button ${activeTab === 'preferences' ? 'active' : ''}`}
              onClick={() => setActiveTab('preferences')}
            >
              <i className="bi bi-person-gear"></i>
              Preferences
            </button>
          </div>

          <div className="tab-content-container">
            {activeTab === 'points' && renderPointsRulesTab()}
            {activeTab === 'countries' && renderCountriesTab()}
            {activeTab === 'referrals' && renderReferralsTab()}
            {activeTab === 'teams' && renderTeamsTab()}
            {activeTab === 'messages' && renderMessagesTab()}
            {activeTab === 'project_creation' && (
              <ProjectCreationSettingsTab />
            )}
            {activeTab === 'preferences' && renderPreferencesTab()}
          </div>
        </div>
      </div>
    </BasePageLayout>
  );
};

export default SettingsPage;